import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Layout, Button, Modal } from "antd";
import { useNavigate } from "react-router";
import { useSelector } from "react-redux";
import {
  FaArrowRight,
  FaCheckCircle,
  FaMedal,
  FaRegClock,
} from "react-icons/fa";
import { GiHamburgerMenu } from "react-icons/gi";
import { ImageUrl } from "../../helpers/Functions";
import "./b2b-training.css";

function Homepage() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.userData);
  const token = useSelector((state) => state.user.userToken);
  const [modal2Open, setModal2Open] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (modal2Open && videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.log("Autoplay failed:", error);
      });
    }
  }, [modal2Open]);

  React.useEffect(() => {
    if (!user && !token) {
      navigate("/", { replace: true });
    }
  }, [user, token, navigate]);

  const data3 = [
    {
      Icon: GiHamburgerMenu,
      title: "Track Enrollment Dates",
      des: "See what date a user enrolled in a course and when they last were active.",
    },
    {
      Icon: FaRegClock,
      title: "Report On Certifications",
      des: "Quickly identify who’s completed a course and received a certification.",
    },
    {
      Icon: FaMedal,
      title: "Offline Reporting",
      des: "Download reports to review offline with colleagues.",
    },
  ];

  const data4 = [
    {
      title: "Log in and train directly after purchasing a course online.",
    },
    {
      title: "Organizations can purchase training in bulk for their company.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>
          Environmental & Facility Health Assessments in Staten Island, NY
        </title>
        <meta
          name="description"
          content="Get expert facility assessments and environmental health evaluations for educational centers in Staten Island, NY. Enhance safety and compliance with WrightCo."
        />
        <meta
          property="og:title"
          content="Environmental & Facility Health Assessments in Staten Island, NY"
        />
        <meta
          property="og:description"
          content="Get expert facility assessments and environmental health evaluations for educational centers in Staten Island, NY. Enhance safety and compliance with WrightCo."
        />
      </Helmet>

      <Layout className="b2b-page">
        <section
          className="b2b-hero"
          style={{ backgroundImage: `url(${ImageUrl("new-18.png")})` }}
        >
          <div className="b2b-hero-overlay" aria-hidden />
          <div className="b2b-wrap b2b-hero-inner">
            <div className="b2b-hero-copy">
              <span className="b2b-hero-badge">LEARNER REPORTS</span>
              <h1 className="fontFamily1 b2b-hero-title">
                Track Learner Progress And Platform Usage{" "}
                <span>In One Place</span>
              </h1>
              <p className="fontFamily1 b2b-hero-sub">
                Keep track of your learners progress and engagement in courses.
                Pull reports on a user’s quiz or course scores, attendance and
                certification records, and more.
              </p>
              <div className="b2b-hero-actions">
                {!token && (
                  <Button
                    type="primary"
                    className="b2b-hero-btn-primary"
                    onClick={() => navigate("/signup")}
                  >
                    Register Now
                  </Button>
                )}
                <Button
                  className="b2b-hero-btn-outline"
                  onClick={() => setModal2Open(true)}
                >
                  See The Platform <FaArrowRight />
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--white">
          <div className="b2b-wrap">
            <div className="b2b-section-intro">
              <h2 className="fontFamily1 b2b-section-title">
                Comprehensive Learner Insights
              </h2>
              <p className="fontFamily1 b2b-section-lead">
                Keep track of how users perform on assessments and how engaged
                they are in course-activities and instructor-led training.
              </p>
            </div>

            <div className="b2b-cards">
              <article className="b2b-card">
                <div className="b2b-card-media">
                  <img src={ImageUrl("new-1.png")} alt="" loading="lazy" />
                </div>
                <div className="b2b-card-body">
                  <h3>Review Assignments And Give Feedback</h3>
                  <p>
                    Review assignment submissions, provide feedback, and give
                    grades directly inside of the LMS.
                  </p>
                  <Button
                    className="b2b-btn-ghost"
                    style={{ marginTop: 14 }}
                    onClick={() => navigate("/pricingPlans")}
                  >
                    See Pricing
                  </Button>
                </div>
              </article>
              <article className="b2b-card">
                <div className="b2b-card-media">
                  <img src={ImageUrl("new-12.png")} alt="" loading="lazy" />
                </div>
                <div className="b2b-card-body">
                  <h3>Track Completions - Scores - Time-Spent</h3>
                  <p>
                    Monitor course completions, assessment scores, certification
                    statuses, and more.
                  </p>
                  <Button
                    className="b2b-btn-ghost"
                    style={{ marginTop: 14 }}
                    onClick={() => navigate("/pricingPlans")}
                  >
                    See Pricing
                  </Button>
                </div>
              </article>
              <article className="b2b-card">
                <div className="b2b-card-media">
                  <img src={ImageUrl("new-13.png")} alt="" loading="lazy" />
                </div>
                <div className="b2b-card-body">
                  <h3>Report On Individual Or Group Progress</h3>
                  <p>
                    Report on learning-related actives in your platform by
                    looking at one User’s progress or progress in a classroom.
                  </p>
                  <Button
                    className="b2b-btn-ghost"
                    style={{ marginTop: 14 }}
                    onClick={() => navigate("/pricingPlans")}
                  >
                    See Pricing
                  </Button>
                </div>
              </article>
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--navy">
          <div className="b2b-wrap">
            <div className="b2b-section-intro">
              <span className="b2b-section-kicker">
                <img src={ImageUrl("miniImg2.png")} alt="" />
              </span>
              <h2 className="fontFamily1 b2b-section-title">
                Monitor Course Engagement With Learner Reports
              </h2>
              <p className="fontFamily1 b2b-section-lead">
                Use robust learner reports to make sure your learners are
                actively completing courses and not wasting time in your
                platform.
              </p>
            </div>

            <div className="b2b-split">
              <div className="b2b-split-media">
                <img src={ImageUrl("new-19.png")} alt="" loading="lazy" />
              </div>
              <div className="b2b-split-copy">
                <ul className="b2b-feature-list">
                  {data3.map((item) => {
                    const Icon = item.Icon;
                    return (
                      <li key={item.title}>
                        <span className="b2b-feature-icon" aria-hidden>
                          <Icon />
                        </span>
                        <div>
                          <h4>{item.title}</h4>
                          <p>{item.des}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--white">
          <div className="b2b-wrap">
            <div className="b2b-split">
              <div className="b2b-split-copy">
                <img
                  src={ImageUrl("miniImg.png")}
                  alt=""
                  className="b2b-section-kicker"
                />
                <h2 className="fontFamily1">
                  Track Your Customers’ Progress Too
                </h2>
                <p>
                  Assign each of your customers to a portal, where they can train
                  and report on the progress of their learners in peace.
                  Customers can generate reports on their users just like you can
                  generate reports on your employees.
                </p>
                <Button
                  className="b2b-btn-ghost"
                  onClick={() => navigate("/pricingPlans")}
                >
                  See Pricing
                </Button>
              </div>
              <div className="b2b-split-media">
                <img src={ImageUrl("new-20.png")} alt="" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--navy">
          <div className="b2b-wrap">
            <div className="b2b-section-intro">
              <span className="b2b-section-kicker">
                <img src={ImageUrl("miniImg2.png")} alt="" />
              </span>
              <h2 className="fontFamily1 b2b-section-title">
                Maintain Security & Compliance With LMS Site Audits
              </h2>
              <p className="fontFamily1 b2b-section-lead">
                Using site audit logs, keep track of every action that ever
                happened in the LMS, along with a detailed record of who
                performed them and what time they occurred.
              </p>
              {!token && (
                <div className="b2b-cta-center">
                  <Button
                    className="b2b-btn-light"
                    onClick={() => navigate("/signup")}
                  >
                    Get Started
                  </Button>
                </div>
              )}
            </div>
            <div
              className="b2b-split-media"
              style={{ maxWidth: 980, margin: "0 auto" }}
            >
              <img src={ImageUrl("new-21.png")} alt="" loading="lazy" />
            </div>
          </div>
        </section>

        <section className="b2b-section b2b-section--muted">
          <div className="b2b-wrap">
            <div className="b2b-split">
              <div className="b2b-split-copy">
                <img
                  src={ImageUrl("miniImg.png")}
                  alt=""
                  className="b2b-section-kicker"
                />
                <h2 className="fontFamily1">Sell Training Course Online</h2>
                <p>
                  Sell accredited training to businesses, individuals, and
                  Continuing Education providers. Sell courses directly on the
                  platform with built-in eCommerce, or integrate with an existing
                  store of yours like Shopify.
                </p>
                <ul className="b2b-check-list">
                  {data4.map((item) => (
                    <li key={item.title}>
                      <FaCheckCircle aria-hidden />
                      <span>{item.title}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  className="b2b-btn-ghost"
                  onClick={() => navigate("/pricingPlans")}
                >
                  See Pricing
                </Button>
              </div>
              <div className="b2b-split-media">
                <img src={ImageUrl("new-16.png")} alt="" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        <section className="b2b-trainer">
          <div className="b2b-wrap">
            <div className="b2b-trainer-card">
              <h3>We&apos;re Looking for Trainers</h3>
              <h3>Teach. Create. Earn.</h3>
              <p>
                Are you passionate about teaching? Ready to share your expertise,
                connect with professionals, and make a meaningful impact, all
                while earning extra income?
              </p>

              <h4>You&apos;ve come to the right place.</h4>
              <p>
                At WrightCo Academy, we are building a network of world-class
                trainers and industry experts dedicated to developing
                high-quality courseware for professionals in early care and
                education. If you&apos;re an expert in your field with a desire
                to teach, we&apos;d love to have you on board!
              </p>

              <h3>Why Join Us?</h3>
              <ul>
                <li>
                  <p>
                    ✅ Expand Your Reach – Share your knowledge with a broad
                    audience of professionals.
                  </p>
                </li>
                <li>
                  <p>
                    ✅ Boost Your Visibility – Gain recognition as a trusted
                    expert in your industry.
                  </p>
                </li>
                <li>
                  <p>
                    ✅ Join a Thriving Community – Connect with like-minded
                    trainers and industry leaders.
                  </p>
                </li>
                <li>
                  <p>
                    ✅ Earn Passive Income – Our fair and flexible revenue-sharing
                    model ensures unlimited earning potential.
                  </p>
                </li>
              </ul>

              <h3>What It Takes to Be a WrightCo Trainer</h3>
              <ul>
                <li>
                  <p>
                    ✔ <strong>Passion for Teaching</strong> – A commitment to
                    sharing knowledge and empowering others.
                  </p>
                </li>
                <li>
                  <p>
                    ✔ <strong>Industry Expertise</strong> – Relevant
                    certifications and experience in your domain.
                  </p>
                </li>
                <li>
                  <p>
                    ✔ <strong>Strong Communication Skills</strong> – Ability to
                    present information effectively.
                  </p>
                </li>
                <li>
                  <p>
                    ✔ <strong>Commitment to Excellence</strong> – Willingness to
                    develop and deliver high-quality training.
                  </p>
                </li>
              </ul>
              <h4>Ready to Make an Impact?</h4>
              <p>
                Join WrightCo Academy today and take the next step in your
                teaching journey.
              </p>
            </div>
          </div>
        </section>

        <Modal
          className="b2b-video-modal"
          centered
          open={modal2Open}
          onOk={() => setModal2Open(false)}
          onCancel={() => setModal2Open(false)}
          width={1000}
          footer={false}
        >
          <div style={{ padding: "24px 12px" }}>
            <video width="auto" height="auto" ref={videoRef} controls>
              <source src="https://react.customdev.solutions:3012/Uploads/tutorVideo.mp4" />
              <source src="movie.ogg" type="video/ogg" />
            </video>
          </div>
        </Modal>
      </Layout>
    </>
  );
}

export default Homepage;
